/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import {
	GenericTable,
	GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { columns as baseColumns, License } from "./LicensingData";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAuth0 } from "@auth0/auth0-react";
import {
	getLicenseIdGlobal,
	getLicenseListByLicense,
	getLicenseListGlobal,
	updateLicense,
} from "./request/request";
import GenericModal from "@ui-components/Modal/GenericModal";
import { format, set } from "date-fns";
import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericInput from "@ui-components/Input/GenericInput";
import { ReactComponent as IconRefresh } from "../../../shared/assets/Icons/IconRefreshV2.svg";
import { ReactComponent as IconEdit } from "../../../shared/assets/Icons/Icon_Edit.svg";
import GenericAlert from "@ui-components/Alert/GenericAlert";
interface LicenseTableItem {
	activated: number;
	description: string;
	enddate: string;
	id: number; // we'll match/fetch details for this license
	serial: string;
	startdate: string;
	subscription: string;
	tenant: number;
	total: number;
	type: string;
}

interface ExtendedLicenseTable extends LicenseTableItem {
	idLicense?: string;
	cloud?: string;
	region?: string;
	activationDate?: string;
}

// The type we store in licenseDetails[row.id]:
interface LicenseDetail {
	idLicense?: string;
	name?: string;
	cloud?: string;
	region?: string;
	activationDate?: string;
	// etc., depending on what the second endpoint returns
}
interface ExpandedLicenseItem {
	id?: string;
	name?: string;
	cloud?: string;
	region?: string;
	activated: string;
}

function LicensingPage() {
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

	// Overlay spinner for the entire page
	const [isFetching, setIsFetching] = useState(false);

	// Main license list (from FIRST endpoint)
	const [licenseList, setLicenseList] = useState<LicenseTableItem[]>([]);
	const [licenseDescription, setLicenseDescription] = useState<string>("");
	const [selectedLicense, setSelectedLicense] =
		useState<LicenseTableItem | null>(null);
	// This is what we actually render in the table
	const [tablesData, setTablesData] = useState<ExtendedLicenseTable[]>([]);

	// We store subscription data here (from getLicenseIdGlobal)
	const [subscription, setSubscription] = useState<string>("");

	// Local filters
	const [searchTerm, setSearchTerm] = useState("");
	const [searchColumn, setSearchColumn] = useState("All");

	// Modal
	const [isModalOpen, setIsModalOpen] = useState(false);
	//Alert
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [alertVariant, setAlertVariant] = useState<"success" | "error">(
		"success"
	);
	const [alertTitle, setAlertTitle] = useState("");
	const [alertDescription, setAlertDescription] = useState("");

	// A dictionary: key = licenseId, value = array of detail items from second endpoint
	const [licenseDetails, setLicenseDetails] = useState<
		Record<number, LicenseDetail[]>
	>({});

	/* Utility function to format dates */
	const formatDate = (isoString: string): string =>
		format(new Date(isoString), "dd-MM-yyyy");

	const fetchLicenseId = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const response = await getLicenseIdGlobal("list", -1, 1, token);
			const subscriptionData: string = response.data || "";
			setSubscription(subscriptionData);
		} catch (err) {
			console.error("Error fetching License subscription:", err);
		} finally {
			setIsFetching(false);
		}
	};

	// 2) Fetch base license list (first endpoint)
	const fetchLicenseList = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();

			const response = await getLicenseListGlobal("1", 1, token);
			const dataArray: LicenseTableItem[] = response.data || [];

			// Format the date fields
			const formattedData = dataArray.map((item) => ({
				...item,
				startdate: formatDate(item.startdate),
				enddate: formatDate(item.enddate),
			}));

			// Store them in your table
			setLicenseList(formattedData);
			setTablesData(formattedData);
		} catch (err) {
			console.error("Error fetching License list:", err);
		} finally {
			setIsFetching(false);
		}
	};
	useEffect(() => {
		if (isLoading || !isAuthenticated) return;

		fetchLicenseId();
		fetchLicenseList();
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	const fetchLicenseDetails = async (row: ExtendedLicenseTable) => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			// Adjust parameters if your endpoint needs them differently
			const response = await getLicenseListByLicense(-1, 1, row.id, token);
			const licenseIdData = response.data || [];

			const shapedData: LicenseDetail[] = licenseIdData.map(
				(item: ExpandedLicenseItem) => ({
					idLicense: item.id || "",
					name: item.name || "",
					cloud: item.cloud || "",
					region: item.region || "",
					activationDate: formatDate(item.activated) || "",
				})
			);

			setLicenseDetails((prev) => ({
				...prev,
				[row.id]: shapedData,
			}));
		} catch (error) {
			console.error("Error fetching second endpoint data:", error);
		} finally {
			setIsFetching(false);
		}
	};

	// Handler: Edit License
	const handleEditLicense = (row: LicenseTableItem) => {
		setSelectedLicense(row);
		setLicenseDescription(row.description);
		setIsModalOpen(true);
	};
	const handleSaveDescription = async () => {
		if (!selectedLicense) return; // Safety check
		try {
			const token = await getAccessTokenSilently();
			await updateLicense(selectedLicense.id, -1, 1, token, licenseDescription);
			await fetchLicenseList();
			setAlertVariant("success");
			setAlertTitle("License Updated!");
			setAlertDescription(`License was successfully updated.`);
			setIsAlertOpen(true);
			setIsModalOpen(false);
		} catch (err) {
			console.error(err);
			setAlertVariant("error");
			setAlertTitle("Update Failed!");
			setAlertDescription(
				"An error occurred while updating the license. Please try again."
			);
			setIsAlertOpen(true);
		}
	};

	const handleRefresh = () => {
		fetchLicenseList();
	};
	// Columns with custom Actions
	const finalColumns: GenericTableColumn<ExtendedLicenseTable>[] =
		baseColumns.map((col) => {
			if (col.header === "Actions") {
				return {
					...col,
					renderCell: (row: ExtendedLicenseTable) => (
						<div className="flex items-center gap-2">
							<button
								className="
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-primary
                text-semantic-fg-secondary
                hover:bg-semantic-state-active
                w-10 h-10 flex items-center justify-center
              "
								onClick={() => handleEditLicense(row)}
							>
								<IconEdit className="w-6 h-6" />
							</button>
						</div>
					),
				};
			}
			return col;
		});

	// Local filter
	const filteredData = tablesData.filter((row) => {
		const term = searchTerm.toLowerCase();
		if (!term) return true;
		const includes = (val: string) => val.toLowerCase().includes(term);

		switch (searchColumn) {
			case "Subscription":
				return includes(row.subscription);
			case "Start Date":
				return includes(row.startdate);
			case "End Date":
				return includes(row.enddate);
			case "License":
				return includes(row.id.toString());
			case "Type":
				return includes(row.type);
			case "Description":
				return includes(row.description);
			case "All":
			default:
				return (
					includes(row.subscription) ||
					includes(row.startdate) ||
					includes(row.enddate) ||
					includes(row.id.toString()) ||
					includes(row.type) ||
					includes(row.description)
				);
		}
	});

	const renderExpandToggle = (
		isExpanded: boolean,
		onToggle: () => void,
		row: ExtendedLicenseTable,
		
	) => {
		void row;
		const handleToggle = async () => {
			if (!isExpanded) {
				try {
					await fetchLicenseDetails(row);
					onToggle();
				} catch (error) {
					console.error("Error fetching license details:", error);
				}
			} else {
				onToggle();
			}
		};
		return (
			<GenericButton
				label={isExpanded ? "Close" : "Devices"}
				variant={isExpanded ? "danger" : "brand"}
				size="sm"
				onClick={handleToggle}
			/>
		);
	};
	// Expanded row renderer
	const renderExpanded = (row: ExtendedLicenseTable) => {
		// Pull the second-endpoint data from our dictionary:
		const details = licenseDetails[row.id];
		if (isFetching || !details) {
			return <div className="text-semantic-text-primary"></div>;
		}
		return (
			<div className="p-4 grid grid-cols-2 gap-4">
				{details.map((item, idx) => (
					<>
						<div
							key={idx}
							className="
              flex flex-col
              p-4
              space-y-2
              bg-semantic-bg-quaternary
              rounded-md
              divide-y divide-semantic-border-tertiary
            "
						>
							<div className="py-1 flex justify-between">
								<span className="font-semibold">Name:</span>
								<span>{item.name || "—"}</span>
							</div>
							<div className="py-1 flex justify-between">
								<span className="font-semibold">ID License:</span>
								<span>{item.idLicense || "—"}</span>
							</div>
							<div className="py-1 flex justify-between">
								<span className="font-semibold">Cloud:</span>
								<span>{item.cloud || "—"}</span>
							</div>
						</div>
						<div
							className="
            flex flex-col
            p-4
            space-y-2
            bg-semantic-bg-quaternary
            rounded-md
            divide-y divide-semantic-border-tertiary"
						>
							<div className="py-1 flex justify-between">
								<span className="font-semibold">Region:</span>
								<span>{item.region || "—"}</span>
							</div>
							<div className="py-1 flex justify-between">
								<span className="font-semibold">Activated:</span>
								<span>{item.activationDate || "—"}</span>
							</div>
						</div>
					</>
				))}
			</div>
		);
	};

	return (
		<div className="relative">
			{/* Local Filter */}
			<div className="mb-4 bg-[var(--semantic-bg-primary)]">
				<div className="flex items-end justify-between">
					<div className="flex items-end space-x-4" />
					<div className="flex items-center space-x-4">
						<GenericButton
							variant="refresh"
							size="sm"
							isIconOnly
							slotLeft={<IconRefresh className="w-4 h-4" />}
							className="mt-[2px] hover:animate-[spin_2s_linear_infinite]"
							aria-label="Refresh"
							onClick={handleRefresh}
						/>
						<GenericFilter
							searchColumn={searchColumn}
							onSearchColumnChange={setSearchColumn}
							searchTerm={searchTerm}
							onSearchTermChange={setSearchTerm}
							columnOptions={[
								"All",
								"Subscription",
								"Start Date",
								"End Date",
								"License",
								"Type",
								"Description",
							]}
						/>
					</div>
				</div>
			</div>

			{/* Table */}
			<GenericTable<ExtendedLicenseTable>
				columns={finalColumns}
				data={filteredData}
				initialRowsPerPage={5}
				rowsPerPageOptions={[5, 10]}
				width="100%"
				tableClassName="bg-semantic-bg-primary"
				expandable
				renderExpanded={renderExpanded}
				renderExpandToggle={renderExpandToggle}
			/>
			<GenericModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title={"Edit License"}
				size="lg"
			>
				<div className="space-y-4">
					<label className="mb-1 text-sm font-medium text-semantic-text-primary">
						Description <span className="text-red-500">*</span>
					</label>
					<GenericInput
						placeholder="Description"
						value={licenseDescription}
						onChange={(newDescription: React.SetStateAction<string>) => setLicenseDescription(newDescription)}
					/>
					<div className="flex items-center w-full justify-center">
						<GenericButton
							label="Save"
							variant="brand"
							size="md"
							onClick={handleSaveDescription}
						/>
					</div>
				</div>
			</GenericModal>
			<GenericAlert
				isOpen={isAlertOpen}
				onClose={() => setIsAlertOpen(false)}
				onConfirm={() => setIsAlertOpen(false)}
				title={alertTitle}
				description={alertDescription}
				cancelLabel="Close"
				confirmLabel="OK"
				variant={alertVariant}
			/>

			{/* Overlay Spinner */}
			{isFetching && (
				<div
					className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
				>
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div
						className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4 border-gray-300
              border-t-blue-500
            "
					/>
				</div>
			)}
		</div>
	);
}

export default LicensingPage;
